import { useQueryClient } from '@tanstack/react-query';

import { useMutation } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import {
	createBenetelNoVesRules,
	deleteBenetelNoVesRules,
	updateBenetelNoVesRules,
} from '@api/alarmManagement';
import { updateBenetelAlarm } from '@api/common';

import { IAlarmOran } from '@interfaces/alarms/alarm';
import { IAlarmSettingsDTO } from '@interfaces/alarms/dto/alarmsDTO';

import { ESnackbarTypes } from '@enums/common';

import {
	ACTIVE_ALARMS,
	ALARM_BENETEL,
	ALARM_HISTORY,
	ALARM_LIST,
	ALARM_LIST_BENETEL,
	EDIT_ALARM,
} from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

export const useMutateUpdateBenetelAlarm = (onSuccess: () => void) => {
	const dispatch = useAppDispatch();
	const queryClient = useQueryClient();

	const {
		mutate: editBenetelAlarm,
		isLoading: isLoadingEditBenetelAlarm,
	} = useMutation<IAlarmSettingsDTO>({
		mutationKey: [EDIT_ALARM],
		mutationFn: updateBenetelAlarm,
		onSuccess: (data: IAlarmOran) => {
			dispatch(setSnackbar({
				message: responseMessages.alarmOranUpdated(data.name),
				type: ESnackbarTypes.success,
			}));

			if (onSuccess) {
				onSuccess();
			}

			return [
				queryClient.invalidateQueries([ALARM_LIST]),
				queryClient.invalidateQueries([ACTIVE_ALARMS]),
				queryClient.invalidateQueries([ALARM_HISTORY]),
				queryClient.invalidateQueries([ALARM_LIST_BENETEL]),
				queryClient.invalidateQueries([ALARM_BENETEL]),
			];
		},
	});

	const updateBenetelAlarmSettings = async (dto: IAlarmSettingsDTO, alarm?: IAlarmOran) => {
		const { networkElementFields, ...rest } = dto;

		if (networkElementFields) {
			const customSettings = alarm?.customSettings?.noVesEventAlarmCreateSettings || [];

			const fieldsToAdd = networkElementFields.filter((field) => !field?.id);

			const fieldsToDelete = customSettings
				.filter((field) => {
					return field?.id && !networkElementFields.find((setting) => setting.id === field.id);
				}).map((field) => ({ id: field.id }));

			const fieldsToUpdate = networkElementFields.filter((field) => {
				return field?.id && customSettings.find((setting) => {
					return setting.id === field.id && setting.noEventMinutes !== field.noEventMinutes;
				});
			})?.map((field) => ({ id: field.id, noEventMinutes: field.noEventMinutes }));

			if (fieldsToDelete.length) await deleteBenetelNoVesRules(fieldsToDelete);
			if (fieldsToUpdate.length) await updateBenetelNoVesRules(fieldsToUpdate);
			if (fieldsToAdd.length) await createBenetelNoVesRules(fieldsToAdd);
		}

		editBenetelAlarm(rest);
	};

	return {
		editBenetelAlarm: updateBenetelAlarmSettings,
		isLoadingEditBenetelAlarm,
	};
};
